import * as React from 'react';

import { type DataGridColumn } from '@rematter/ui-web';

import { type PeriodBalanceRecord } from '../../_graphql/queryInventoryPeriodBalances';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';

import { getLocale } from '~/shared/utils/locale';

const formatCurrency = (amount: number, currencyCode: string): string =>
  new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);

export const usePeriodBalanceColumns =
  (): DataGridColumn<PeriodBalanceRecord>[] => {
    const t = useTranslations();
    const { formatQuantityValue } = useInventoryUnit();

    return React.useMemo(
      () => [
        {
          key: 'item',
          header: t.periodsListView.detail.balanceTable.columns.item,
          cell: ({ row }) => (
            <span className="font-medium">{row.itemName}</span>
          ),
          width: 200,
        },
        {
          key: 'account',
          header: t.periodsListView.detail.balanceTable.columns.account,
          cell: ({ row }) => <span className="text-sm">{row.accountName}</span>,
          width: 180,
        },
        {
          key: 'quantity',
          header: t.periodsListView.detail.balanceTable.columns.quantity,
          cell: ({ row }) => (
            <span className="text-sm tabular-nums">
              {formatQuantityValue(row.quantity, row.quantityUnit)}
            </span>
          ),
          width: 120,
        },
        {
          key: 'wacPerUnit',
          header: t.periodsListView.detail.balanceTable.columns.wacPerUnit,
          cell: ({ row }) => (
            <span className="text-sm tabular-nums">
              {formatCurrency(row.wacPerUnit, row.currencyCode)}
            </span>
          ),
          width: 140,
        },
        {
          key: 'totalValue',
          header: t.periodsListView.detail.balanceTable.columns.totalValue,
          cell: ({ row }) => (
            <span className="text-sm tabular-nums">
              {formatCurrency(row.totalValue, row.currencyCode)}
            </span>
          ),
          width: 140,
        },
      ],
      [t, formatQuantityValue],
    );
  };
